/* eslint-disable max-lines */
import { Role } from '@hakimo-ui/hakimo/types';

export enum ScreenWidth {
  sm = 640,
  md = 768,
  lg = 1024,
  xl = 1280,
  '2xl' = 1536,
}

export const ROLE_MSP_OPERATOR: Role = 'MSP Operator';

export const WEEKDAYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

export const getWeekdays = () =>
  WEEKDAYS.map((day) => ({ name: day, symbol: day[0] }));

export const convertToMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const convertToSeconds = (time: string): number => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours * 60 * 60 + minutes * 60 + (seconds ?? 0);
};

const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;

export const isValidTime = (time: string): boolean =>
  TIME_REGEX.test(time ?? '');

export const withSeconds = (time: string): string => {
  const parts = time.split(':');
  if (parts.length === 2) {
    parts.push('00');
  }
  return parts.join(':');
};

const START_OF_DAY_SEC = 0;
const END_OF_DAY_SEC = 23 * 3600 + 59 * 60 + 59;

export interface DailySegment {
  startSec: number;
  endSec: number;
  isOverflow: boolean;
}

export const splitDailyWindow = (
  startTime: string,
  endTime: string
): DailySegment[] => {
  if (!isValidTime(startTime) || !isValidTime(endTime)) {
    return [];
  }

  const startSec = convertToSeconds(startTime);
  const endSec = convertToSeconds(endTime);

  if (endSec > startSec) {
    return [{ startSec, endSec, isOverflow: false }];
  }

  const segments: DailySegment[] = [
    { startSec, endSec: END_OF_DAY_SEC, isOverflow: false },
  ];
  if (endSec < startSec || startSec !== START_OF_DAY_SEC) {
    segments.push({ startSec: START_OF_DAY_SEC, endSec, isOverflow: true });
  }
  return segments;
};

export const RESOLUTION_REASONS = [
  { id: 'Arrest Made', name: 'Arrest Made' },
  { id: 'Police Engaged Subject', name: 'Police Engaged Subject' },
  { id: 'Security Engaged Subject', name: 'Security Engaged Subject' },
  {
    id: 'Emergency Services Responded',
    name: 'Emergency Services Responded',
  },
  {
    id: 'Responder Found No Subject',
    name: 'Responder Found No Subject',
  },
  {
    id: 'Subject Deterred by Talkdown',
    name: 'Subject Deterred by Talkdown',
  },
  { id: 'Subject Left on Own', name: 'Subject Left on Own' },
  { id: 'Site Secured', name: 'Site Secured' },
  {
    id: 'Authorized Activity Confirmed',
    name: 'Authorized Activity Confirmed',
  },
  { id: 'Notification Only', name: 'Notification Only' },
  { id: 'Closed – No Response', name: 'Closed – No Response' },
  { id: 'False Alarm / No Threat', name: 'False Alarm / No Threat' },
  { id: 'Others', name: 'Others' },
];

export const INCIDENT_CATEGORIES = [
  { id: 'Animal & Pet Violation', name: 'Animal & Pet Violation' },
  { id: 'BOLO & Wanted Individuals', name: 'BOLO & Wanted Individuals' },
  { id: 'Burglary & Forced Entry', name: 'Burglary & Forced Entry' },
  { id: 'Crowd Gathering & Protest', name: 'Crowd Gathering & Protest' },
  {
    id: 'Disturbance & Physical Altercation',
    name: 'Disturbance & Physical Altercation',
  },
  {
    id: 'Door & Gate Security Violation',
    name: 'Door & Gate Security Violation',
  },
  { id: 'Drugs & Substance Use', name: 'Drugs & Substance Use' },
  {
    id: 'Employees & Authorized Personnel',
    name: 'Employees & Authorized Personnel',
  },
  {
    id: 'Fire & Emergency Services',
    name: 'Fire & Emergency Services',
  },
  { id: 'Law Enforcement Onsite', name: 'Law Enforcement Onsite' },
  { id: 'Loitering', name: 'Loitering' },
  { id: 'Medical Emergency', name: 'Medical Emergency' },
  { id: 'Other', name: 'Other' },
  { id: 'Package & Mail Theft', name: 'Package & Mail Theft' },
  { id: 'Public Nuisance & Indecency', name: 'Public Nuisance & Indecency' },
  { id: 'Queue Management', name: 'Queue Management' },
  {
    id: 'Security Guard Performance',
    name: 'Security Guard Performance',
  },
  { id: 'Smoking & Vaping', name: 'Smoking & Vaping' },
  {
    id: 'Suspicious Behavior/Prowling',
    name: 'Suspicious Behavior/Prowling',
  },
  {
    id: 'System Testing & False Alarm',
    name: 'System Testing & False Alarm',
  },
  { id: 'Tailgating', name: 'Tailgating' },
  { id: 'Theft & Larceny', name: 'Theft & Larceny' },
  { id: 'Trash & Illegal Dumping', name: 'Trash & Illegal Dumping' },
  {
    id: 'Trespassing & Unauthorized Entry',
    name: 'Trespassing & Unauthorized Entry',
  },
  { id: 'Unaccompanied Minors', name: 'Unaccompanied Minors' },
  {
    id: 'Unauthorized After-Hours Access',
    name: 'Unauthorized After-Hours Access',
  },
  {
    id: 'Vagrancy & Homeless Activity',
    name: 'Vagrancy & Homeless Activity',
  },
  { id: 'Vandalism & Property Damage', name: 'Vandalism & Property Damage' },
  { id: 'Vehicle & Parking Violation', name: 'Vehicle & Parking Violation' },
  { id: 'Vehicle Break-in', name: 'Vehicle Break-in' },
  { id: 'Weapon', name: 'Weapon' },
  { id: 'Workplace Safety Violation', name: 'Workplace Safety Violation' },
];

export function getPreviousDayOfWeek(day: string): string {
  const dayIndex = WEEKDAYS.indexOf(day);

  // Check if the day is valid
  if (dayIndex === -1) {
    throw new Error(
      `Invalid day: ${day}. Must be one of ${WEEKDAYS.join(', ')}`
    );
  }

  // Calculate the previous day index with wraparound to SATURDAY if day is SUNDAY
  const previousDayIndex = (dayIndex - 1 + WEEKDAYS.length) % WEEKDAYS.length;

  return WEEKDAYS[previousDayIndex];
}

export const DEFAULT_AUDIO_NOTIF_FILE = 'notif-sound-default';
export const ESCALATION_AUDIO_NOTIF_FILE = 'notif-sound-5';
export const tagOptions = ['Police', 'Guards', 'SPOC', 'RSPNDR'];
export const NOTIFICATION_SOUND_LIST = [
  'notif-sound-1',
  'notif-sound-2',
  'notif-sound-3',
  'notif-sound-4',
  'notif-sound-5',
];

export const personDetectedOptions = [
  'Authorized Employee(s)/Cleaner(s)',
  'Authorized Security Guard',
  'Authorized Resident',
  'Law Enforcement on site',
  'Person Off-site',
  'Deterred with Talkdown',
  'Other',
];

export const vehicleDetectedOptions = [
  'Employee Vehicle',
  'Security Vehicle',
  'Law Enforcement Vehicle',
  'Delivery Truck',
  'Vehicle Off-site',
  'Deterred with Talkdown',
  'Other',
];

export const incorrectDetectionOptions = [
  'No Person',
  'No Vehicle movement / Stationary Vehicle',
  'Animal onsite',
  'Other',
];

export const correctDetectionOptions = [
  'Unauthorised person',
  'Crowd / Protest',
  'Weapon',
  'Others',
];

export const incorrectDetectionV2Options = [
  'False Positive - Unauthorised person',
  'False Positive - Crowd / Protest',
  'False Positive - Weapon',
  'False Positive - Others',
];
